/**
 * User Types - Frontend
 * @description User-related types matching backend DTOs
 */

import { ID, PaginationParams, Timestamp } from './api.types';

// Enums
export enum UserRole {
  USER = 'Operario',
  OPERATOR = 'Supervisor',
  ADMIN = 'Administrador',
}

export enum UserStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  DELETED = 'deleted',
}

export enum TimeFormat {
  HOURS_12 = '12h',
  HOURS_24 = '24h',
}

// User Response DTO (what the API returns)
export interface User {
  id: ID;
  personalInfo: {
    name: string;
    middlename?: string;
    surname1: string;
    surname2?: string;
    company?: string;
    email1?: string;
    email2?: string;
    phone1?: string;
    phone2?: string;
  };
  credentials: {
    username: string;
    passwordLastChanged?: Timestamp;
    requiresPasswordChange?: boolean;
  };
  role: UserRole;
  timezone: string;
  deviceAccess?: Array<{
    deviceId: string;
    typeOfAccess: 'Master' | 'Viewer' | 'Operator';
  }>;
  sessionManagement: {
    tokens: string[];
    lastLogin?: Timestamp;
    /** IANA timezone recorded at last successful login */
    lastLoginTz?: string;
    activeSessions: unknown[];
  };
  securitySettings: {
    mfaEnabled: boolean;
    mfaMethod?: string;
    mfaBackupCodes?: string[];
  };
  preferences: {
    timezone: string;
    dateFormat: string;
    timeFormat: TimeFormat;
    language?: string;
    notifications?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
  };
  rateLimit: {
    maxRequestsPerMinute?: number;
    currentCount: number;
    resetAt?: Timestamp;
  };
  metadata: {
    version: number;
    status?: UserStatus;
    resetPasswordToken?: string;
    resetPasswordExpires?: Timestamp;
    confirmationToken?: string;
    confirmationTokenExpires?: Timestamp;
    accountConfirmed?: boolean;
    accountConfirmedAt?: Timestamp;
    createdAt?: Timestamp;
    createdBy?: string;
    updatedAt?: Timestamp;
    updatedBy?: string;
    subscription?: 'active' | 'inactive';
    subscriptionExpiryDate?: Timestamp;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Auth Response DTO
export interface AuthResponse {
  token: string;
  refreshToken?: string;
  expiresIn?: number;
  user: {
    id: ID;
    username: string;
    role: UserRole;
    name: string;
    surname: string;
    requiresPasswordChange?: boolean;
  };
}

// User Create DTO (for creating users)
export interface UserCreateRequest {
  personalInfo: {
    name: string;
    middlename?: string;
    surname1: string;
    surname2?: string;
    company?: string;
    email1?: string;
    email2?: string;
    phone1?: string;
    phone2?: string;
  };
  credentials: {
    username: string;
    password?: string;
    requiresPasswordChange?: boolean;
  };
  role?: UserRole;
  timezone: string;
  preferences?: {
    timezone: string;
    dateFormat: string;
    timeFormat: TimeFormat;
    language?: string;
    notifications?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
  };
  deviceAccess?: Array<{
    deviceId: string;
    typeOfAccess: 'Master' | 'Viewer' | 'Operator';
  }>;
  securitySettings?: {
    mfaEnabled: boolean;
    mfaMethod?: string;
    mfaBackupCodes?: string[];
  };
  // Subscription data
  subscription?: 'active' | 'inactive';
  subscriptionExpiryDate?: Timestamp;
}

// User Update DTO (for updating users)
export interface UserUpdateRequest {
  id: string;
  personalInfo?: Partial<{
    name: string;
    middlename?: string;
    surname1: string;
    surname2?: string;
    company?: string;
    email1?: string;
    email2?: string;
    phone1?: string;
    phone2?: string;
  }>;
  credentials?: Partial<{
    username: string;
    requiresPasswordChange?: boolean;
  }>;
  role?: UserRole;
  timezone?: string;
  preferences?: Partial<{
    timezone: string;
    dateFormat: string;
    timeFormat: TimeFormat;
    language?: string;
    notifications?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
  }>;
  deviceAccess?: Array<{
    deviceId: string;
    typeOfAccess: 'Master' | 'Viewer' | 'Operator';
  }>;
  securitySettings?: Partial<{
    mfaEnabled: boolean;
    mfaMethod?: string;
    mfaBackupCodes?: string[];
  }>;
  // Subscription data
  subscription?: 'active' | 'inactive';
  subscriptionExpiryDate?: Timestamp;
}

export type UserFormData = Omit<
  User,
  '_id' | 'createdAt' | 'updatedAt' | 'sessionManagement' | 'rateLimit' | 'securitySettings'
> & {
  credentials: User['credentials'] & {
    password?: string;
  };
  metadata: {
    version: number;
    subscription?: 'active' | 'inactive';
    subscriptionExpiryDate?: Timestamp;
  };
};

export interface UserFilters {
  name?: string;
  surname1?: string;
  username?: string;
  role?: UserRole;
  status?: UserStatus;
}

export interface UpdatePasswordRequest {
  newPassword: string;
}

export interface UserQueryParams extends Omit<UserFilters, 'role' | 'status'>, PaginationParams {
  role?: string;
  status?: UserStatus;
  permission?: string;
}
